import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from './api';
import { Plus, Search, LogOut, Trash2, User, ChevronRight, X } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_bbc14d05-1309-4872-bdcf-991a413c9c8e/artifacts/9i1h196a_JCS_monograma_transparente.png';

const EMPTY_FORM = { name: '', nif: '', address: '', phone: '', email: '', notes: '' };

export default function Dashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const r = await api.get('/admin/clients');
      setClients(r.data);
    } catch {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/admin/clients', form);
      setForm(EMPTY_FORM);
      setShowForm(false);
      fetchClients();
    } catch {}
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/clients/${id}`);
      setDeleteId(null);
      fetchClients();
    } catch {}
  };

  const filtered = clients.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.nif?.includes(search) ||
    c.phone?.includes(search)
  );

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E0D8] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="JCS" className="h-8 w-auto" />
            <div>
              <p className="font-serif text-base text-[#1A1A1A] leading-tight">Portal JCS</p>
              <p className="font-sans text-xs text-[#AAAAAA] tracking-widest uppercase">Área de Trabalho</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-2 font-sans text-xs text-[#AAAAAA] hover:text-[#6B2D3E] transition-colors uppercase tracking-widest"
          >
            <LogOut size={14} /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h2 className="font-serif text-2xl text-[#1A1A1A]">Clientes</h2>
            <p className="font-sans text-sm text-[#AAAAAA]">{clients.length} cliente{clients.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Pesquisar..."
                data-testid="client-search"
                className="pl-8 pr-4 py-2 border border-[#E5E0D8] bg-white font-sans text-sm focus:outline-none focus:border-[#6B2D3E] w-52 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowForm(true)}
              data-testid="new-client-btn"
              className="flex items-center gap-2 bg-[#6B2D3E] text-white font-sans text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#5A2535] transition-colors"
            >
              <Plus size={14} /> Novo Cliente
            </button>
          </div>
        </div>

        {/* Client list */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-[#E5E0D8] p-16 text-center">
            <User size={28} className="text-[#CCCCCC] mx-auto mb-3" />
            <p className="font-sans text-sm text-[#AAAAAA]">
              {search ? 'Nenhum cliente encontrado.' : 'Ainda não há clientes. Crie o primeiro!'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E0D8] divide-y divide-[#F4F2EE]">
            {filtered.map(c => (
              <div
                key={c.id}
                className="flex items-center justify-between px-5 py-4 hover:bg-[#FDFBF7] transition-colors group"
                data-testid={`client-row-${c.id}`}
              >
                <button
                  className="flex items-center gap-4 flex-1 text-left"
                  onClick={() => navigate(`/escritorio/cliente/${c.id}`)}
                >
                  <div className="w-9 h-9 rounded-full bg-[#F4F2EE] flex items-center justify-center flex-shrink-0">
                    <span className="font-serif text-sm text-[#6B2D3E]">{c.name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="font-sans text-sm font-medium text-[#1A1A1A]">{c.name}</p>
                    <p className="font-sans text-xs text-[#AAAAAA]">
                      {c.nif ? `NIF: ${c.nif}` : ''}{c.nif && c.phone ? ' · ' : ''}{c.phone || ''}
                    </p>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <ChevronRight size={16} className="text-[#CCCCCC] group-hover:text-[#6B2D3E] transition-colors" />
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteId(c.id); }}
                    data-testid={`delete-client-${c.id}`}
                    className="p-1.5 text-[#CCCCCC] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* New Client Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" data-testid="new-client-modal">
          <div className="bg-white w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E0D8]">
              <h3 className="font-serif text-lg text-[#1A1A1A]">Novo Cliente</h3>
              <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} className="text-[#AAAAAA] hover:text-[#1A1A1A]">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {[
                { key: 'name', label: 'Nome *', required: true, type: 'text' },
                { key: 'nif', label: 'NIF', type: 'text' },
                { key: 'phone', label: 'Telefone', type: 'tel' },
                { key: 'email', label: 'Email', type: 'email' },
                { key: 'address', label: 'Morada', type: 'text' },
              ].map(f => (
                <div key={f.key}>
                  <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">{f.label}</label>
                  <input
                    type={f.type}
                    required={f.required}
                    value={form[f.key]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    data-testid={`new-client-${f.key}`}
                    className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E] transition-colors"
                  />
                </div>
              ))}
              <div>
                <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">Notas</label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E] resize-none transition-colors"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}
                  className="flex-1 py-2.5 border border-[#E5E0D8] font-sans text-xs uppercase tracking-widest text-[#4A4A4A] hover:bg-[#F4F2EE] transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  data-testid="save-new-client-btn"
                  className="flex-1 py-2.5 bg-[#6B2D3E] text-white font-sans text-xs uppercase tracking-widest hover:bg-[#5A2535] transition-colors disabled:opacity-50">
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white w-full max-w-sm shadow-xl p-6">
            <h3 className="font-serif text-lg text-[#1A1A1A] mb-2">Eliminar Cliente</h3>
            <p className="font-sans text-sm text-[#4A4A4A] mb-6">
              Tem a certeza? Todos os processos, honorários e documentos serão eliminados permanentemente.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)}
                className="flex-1 py-2.5 border border-[#E5E0D8] font-sans text-xs uppercase tracking-widest hover:bg-[#F4F2EE] transition-colors">
                Cancelar
              </button>
              <button onClick={() => handleDelete(deleteId)} data-testid="confirm-delete-btn"
                className="flex-1 py-2.5 bg-red-600 text-white font-sans text-xs uppercase tracking-widest hover:bg-red-700 transition-colors">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
